import {
  ApolloClient,
  createHttpLink,
  defaultDataIdFromObject,
  InMemoryCache,
  NormalizedCacheObject,
  split
} from '@apollo/client'
import {setContext} from '@apollo/client/link/context'
import {onError} from '@apollo/client/link/error'
import {GraphQLWsLink} from '@apollo/client/link/subscriptions'
import {getMainDefinition} from '@apollo/client/utilities'
import {createClient as createWsClient} from 'graphql-ws'
import {init as initBugsnag, notify} from './bugsnag'
import {Authenticator, makeAuthenticator} from './cognito'
import {APP_FLAVOUR, CUSTOMER_API_URL, ENVIRONMENT_NAME} from './env'

let auth: Authenticator
const clients: {[orgId: string]: ApolloClient<NormalizedCacheObject>} = {}

export const ERR_NO_ORGANISATIONS = 'No organisations found'

export const getAuth = () => {
  if (!auth) {
    auth = makeAuthenticator(APP_FLAVOUR)

    auth.on('stateChange', authState => {
      const bugsnag = initBugsnag()
      if (bugsnag) {
        if (authState.authenticated) {
          bugsnag.setUser(authState.sub)
        } else {
          bugsnag.setUser()
        }
      }
    })

    auth.on('signOut', () => {
      for (const client of Object.values(clients)) {
        client.cache.reset()
      }
    })
  }
  return auth
}

export const getToken = () =>
  getAuth()
    .getIdToken()
    .catch(e => {
      if (
        (e instanceof Error && /not logged in/.test(e.message)) ||
        /not logged in/.test(String(e))
      ) {
        console.warn(e)
        return undefined
      } else {
        throw e
      }
    })

const authLink = setContext(async (_, {headers}) => {
  const token = getAuth().getAuthState().authenticated
    ? await getAuth().getIdToken()
    : undefined

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : ''
    }
  }
})

/**
 * Constructs a GraphQL endpoint URL for the given organisation.
 * @param organisationId The organisation ID
 * @param options Optional: {suffix, protocolSwap}
 *   suffix: string to append to the URL (e.g. ?operationName)
 *   protocolSwap: if set, replaces the protocol (e.g. 'ws' for WebSocket)
 */
const makeGraphqlUrl = (
  organisationId: string,
  options?: {suffix?: string; useWSProtocol?: boolean}
) => {
  const url = new URL(organisationUri(organisationId))
  // eslint-disable-next-line functional/immutable-data
  url.pathname = `${url.pathname.replace(/\/$/, '')}/workbench/graphql`

  if (options?.useWSProtocol) {
    // eslint-disable-next-line functional/immutable-data
    url.protocol = url.protocol.replace(/^http/, 'ws')
  }
  const urlStr = url.toString()
  if (options?.suffix) {
    return urlStr + options.suffix
  }
  return urlStr
}

const uriLink = (organisationId: string) =>
  setContext(({operationName}, context) => {
    // If context.uri is explicity passed as an option, e.g. as in accept-invitation.tsx
    // then we want it to take precedence
    if (context.uri) {
      return context
    }
    try {
      // Add the operation name so it's easier to identify graphql requests
      // in the browser devtools - this is ignored by the server
      const suffix = ['development', 'pollution', 'local'].includes(
        ENVIRONMENT_NAME
      )
        ? `?${operationName}`
        : ''
      return {
        uri: makeGraphqlUrl(organisationId, {suffix})
      }
    } catch (e) {
      if (e instanceof Error && e.message === ERR_NO_ORGANISATIONS) {
        return context
      }
      throw e
    }
  })

const errorLink = onError(({graphQLErrors, networkError}) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({message, locations, path, ...meta}) => {
      const logMessage = `[Apollo GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      notify(logMessage, meta)
      console.error(logMessage)
    })
  }

  if (networkError) {
    const message =
      'bodyText' in networkError
        ? `[Apollo Network error]: ${networkError.bodyText} `
        : `[Apollo Network error]: ${networkError}`

    // If we don't override the message here when have bodyText ( see ServerParserError type, error without json )
    // the only message you get is a json parse error
    if ('bodyText' in networkError) {
      networkError.message = message
    }

    notify(message, networkError)
    console.error(message)
  }
})

const organisationIdHeader = (organisationId: string) =>
  setContext((_, {headers}) => {
    const isLocalhost = ENVIRONMENT_NAME === 'local'
    return {
      headers: {
        ...headers,
        ...(isLocalhost
          ? {'x-atomic-test-host': `${organisationId}.customer-api.atomic.io`}
          : {})
      }
    }
  })

const cache = new InMemoryCache({
  dataIdFromObject: object =>
    'nodeId' in object
      ? `${object.__typename}:${object.nodeId}`
      : defaultDataIdFromObject(object)
})

const timezoneHeader = () =>
  setContext((_, {headers}) => {
    return {
      headers: {
        ...headers,
        'x-atomic-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    }
  })

// NOTE: The websocket link doesn't have access to the context (at least as far as I can tell)
// so we have to reproduce any of the applicable logic from the prior context setters (e.g. authLink)
// https://github.com/apollographql/apollo-client/issues/3967
const splitProtocolLink = (organisationId: string) => {
  const wsLink = new GraphQLWsLink(
    createWsClient({
      // Headers can't be set on ws requests, connectionParams are passed with the init message
      // and processed by the server as though they were set in the headers
      connectionParams: async () => {
        const token = getAuth().getAuthState().authenticated
          ? await getAuth().getIdToken()
          : undefined

        const isLocalhost = ENVIRONMENT_NAME === 'local'

        return {
          authorization: token ? `Bearer ${token}` : '',
          ...(isLocalhost
            ? {'x-atomic-test-host': `${organisationId}.customer-api.atomic.io`}
            : {}),
          'x-atomic-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      },
      url: () => makeGraphqlUrl(organisationId, {useWSProtocol: true}),
      on: {
        error: e => {
          console.warn('Websocket subscription failed with error', e)
          notify('WebSocket error', e instanceof Error ? {...e} : {})
        }
      },
      // This is fairly arbitrary - may need to be tweaked
      retryAttempts: 5,
      retryWait: async retries => {
        // Retry after 0.5 second, 1, 2, 4 etc.
        const wait = Math.min(500 * Math.pow(2, retries + 1), 10_000)
        await new Promise(resolve => setTimeout(resolve, wait))
      }
    })
  )
  return split(
    ({query}) => {
      const definition = getMainDefinition(query)
      // Only send subscriptions to the websocket link (i.e. queries & mutations still go via http)
      const isSubscription =
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      return isSubscription
    },
    wsLink,
    createHttpLink()
  )
}

export const getClient = (organisationId: string) => {
  if (!clients[organisationId]) {
    // eslint-disable-next-line functional/immutable-data
    clients[organisationId] = new ApolloClient({
      link: authLink
        .concat(errorLink)
        .concat(uriLink(organisationId))
        .concat(organisationIdHeader(organisationId))
        .concat(timezoneHeader())
        .concat(splitProtocolLink(organisationId)),
      cache,
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          nextFetchPolicy: 'cache-first'
        }
      }
    })
  }
  return clients[organisationId]
}

export const organisationUri = (organisationId: string) =>
  CUSTOMER_API_URL.includes('app.github.dev')
    ? CUSTOMER_API_URL
    : getApiUrl(CUSTOMER_API_URL)(organisationId) // prefix a url with organisation id

export const getApiUrl = (baseUrl: string) => (organisationId: string) =>
  baseUrl.replace(/^https:\/\//, `https://${organisationId}.`)
